// Fetch all matchups for the league in the current season
const getAllMatchups = async (leagueId, season, week) => {
    const base_url = 'lm-api-reads.fantasy.espn.com';
    const weekParam = week ? `&scoringPeriodId=${week}` : ``;
    const url = `https://${base_url}/apis/v3/games/ffl/seasons/${season}/segments/0/leagues/${leagueId}?view=mBoxscore&view=mMatchupScore${weekParam}`;
    const options = {
        method: 'GET',
        // Add headers like cookies if needed
    };
    const res = await fetch(url, options);
    const json = await res.json();

    // Return the relevant data or handle errors
    return json.schedule && json.status ? json : null;
};

// "W-L", or "W-L-T" when the team has a tie. Empty when ESPN sends no record.
export const formatRecord = record => {
    if (!record) return '';
    return record.ties > 0
        ? `${record.wins}-${record.losses}-${record.ties}`
        : `${record.wins}-${record.losses}`;
};

// A matchup side can be absent (bye weeks, and the orphaned matchups that appear
// once teams are eliminated), and a side can point at a team missing from the
// teams list. Return null in those cases so callers skip the side rather than
// crashing the whole scoreboard.
const withTeamDetails = (side, teamMap) => {
    if (!side) return null;
    const team = teamMap[side.teamId];
    return {
        ...side,
        teamName: team?.name ?? '',
        teamLogo: team?.logoUrl ?? '',
        teamRecord: team?.record ?? '',
    };
};

// Get all current week's games with team details
const getWeekGamesWithTeams = (matchupData, week) => {
    const { schedule, status, teams } = matchupData;
    // const { schedule, status, teams } = matchupData;

    // Map teamId to team details (name, logo)
    const teamMap = {};
    teams.forEach(team => {
        teamMap[team.id] = {
            name: team.name,
            logoUrl: team.logo || '',                   // Assuming `logo` contains the URL
            record: formatRecord(team.record?.overall),
        };
    });

    // console.log(status);
    // if week is passed use that, if not default to current week
    const requestedWeek = week ? week : status.currentMatchupPeriod;
    // Get games for the current week and add team details
    return schedule
        .filter(game => game.matchupPeriodId === requestedWeek)
        .map(game => ({
            ...game,
            // The NFL week the rosters are for, to look up players' games.
            scoringPeriodId: matchupData.scoringPeriodId,
            home: withTeamDetails(game.home, teamMap),
            away: withTeamDetails(game.away, teamMap),
        }));
};

// Filter the matchups to get the specific team's boxscore
const getTeamBoxscore = (currentWeekGames, teamId) => {
    const matchupForTeam = currentWeekGames.find(
        game => game.home.teamId === teamId || game.away.teamId === teamId
    );

    return matchupForTeam || null;
};

// Combined function to get the current boxscore for a specific team
export const getBoxscore = async (leagueId, season, teamId, week) => {
    const matchupData = await getAllMatchups(leagueId, season);
    
    if (matchupData) {
        const currentWeekGames = getWeekGamesWithTeams(matchupData, week);
        const teamMatchup = getTeamBoxscore(currentWeekGames, teamId);

        return {
            team: matchupData.teams.find(team => team.id === teamId),
            boxscore: teamMatchup
        };
    } else {
        return { error: "No matchup data found" };
    }
};

// Lightweight fetch of a league's teams for the "connect your league" flow.
// Uses the mTeam view (no boxscores) to list teams for team selection.
// Returns { leagueName, teams } for public leagues, or null if the league is
// private/invalid (ESPN returns an error payload without a `teams` array).
export const getLeagueTeams = async (leagueId, season) => {
    const base_url = 'lm-api-reads.fantasy.espn.com';
    const url = `https://${base_url}/apis/v3/games/ffl/seasons/${season}/segments/0/leagues/${leagueId}?view=mTeam`;
    try {
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) return null;
        const json = await res.json();
        if (!json.teams || json.teams.length === 0) return null;
        return {
            leagueName: json.settings?.name || '',
            teams: json.teams,
        };
    } catch (error) {
        return null;
    }
};

// Every NFL game in the season, flattened to { id, date, statsOfficial,
// scoringPeriodId, homeProTeamId, awayProTeamId }.
// `date` is the kickoff in epoch ms; ESPN flips `statsOfficial` shortly after
// the final whistle. The league endpoint won't return this view alongside
// mBoxscore, so it's a separate (small, browser-cached) request.
export const getProGames = async season => {
    const base_url = 'lm-api-reads.fantasy.espn.com';
    const url = `https://${base_url}/apis/v3/games/ffl/seasons/${season}?view=proTeamSchedules_wl`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return [];
    const json = await res.json();

    // Each game is listed under both of its teams, so de-duplicate by id.
    const games = {};
    (json.settings?.proTeams ?? []).forEach(team => {
        Object.values(team.proGamesByScoringPeriod ?? {}).flat().forEach(game => {
            games[game.id] = {
                id: game.id,
                date: game.date,
                statsOfficial: game.statsOfficial,
                scoringPeriodId: game.scoringPeriodId,
                homeProTeamId: game.homeProTeamId,
                awayProTeamId: game.awayProTeamId,
            };
        });
    });
    return Object.values(games);
};

// Example function to get all matchups for the current week (for a scoreboard)
export const getWeekScoreboard = async (leagueId, season, week) => {
    const matchupData = await getAllMatchups(leagueId, season, week);

    if (matchupData) {
        return getWeekGamesWithTeams(matchupData, week);
    } else {
        return { error: "No matchup data found" };
    }
};